import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import PlaceBetOpenBets from "../component/common/PlaceBetOpenBets";
import MatchRulesModal from "../component/common/MatchRulesModal";
import { toast } from "react-toastify";
import {
  clearSelectedBet,
  fetchFanciesAsync,
  fetchFancyLiabilityAsync,
  fetchMatchDetailsAsync,
  fetchTeamPositionAsync,
  setSelectedBet,
} from "../store/slices/gameSlice";
import { getRacingDetailFanciesAndMatchDetailsInterval } from "../env";
import { fetchOpenBetsAsync } from "../store/slices/betSlice";
import { isAuthenticated } from "../utils/auth";
import { toggleFavoriteMarket } from "../store/slices/sportEventSlice";
import { fetchMultiMarketDetails } from "../store/slices/multiMarketSlice";

const RACE_IMG_BASE =
  "https://content-cache.cdnbf.net/feeds_images/Horses/SilkColours/c20260307-Albion Park (AUS)-H/";

const formatDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
};

const getDisplayCountdown = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0) return "00:00";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(
      seconds,
    ).padStart(2, "0")}`;
  }
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const formatLadder = (sideRaw) => {
  const side = Array.isArray(sideRaw) ? sideRaw.slice(0, 3) : [];
  const prices = [];
  const sizes = [];

  for (let i = 0; i < 3; i += 1) {
    const row = side[i];
    if (row && typeof row === "object") {
      const price = row.price ?? row.odds ?? row.rate ?? row[0] ?? 0;
      const size = row.size ?? row.amount ?? row.volume ?? row[1] ?? 0;
      prices.push(String(price ?? 0));
      sizes.push(String(size ?? 0));
    } else if (row != null) {
      prices.push(String(row));
      sizes.push("0");
    } else {
      prices.push("0");
      sizes.push("0");
    }
  }

  return { prices, sizes };
};

const useShortPolling = (callback, intervalMs, enabled = true) => {
  useEffect(() => {
    if (!enabled || typeof intervalMs !== "number" || intervalMs <= 0) return;
    const id = setInterval(() => {
      callback();
    }, intervalMs);
    return () => clearInterval(id);
  }, [callback, intervalMs, enabled]);
};

const useFlashOnChange = (value, duration = 400) => {
  const [flash, setFlash] = React.useState(false);
  const prevRef = React.useRef(JSON.stringify(value));

  React.useEffect(() => {
    const nextSerialized = JSON.stringify(value);
    if (prevRef.current === nextSerialized) return;
    prevRef.current = nextSerialized;
    setFlash(true);
    const id = setTimeout(() => setFlash(false), duration);
    return () => clearTimeout(id);
  }, [value, duration]);

  return flash;
};

const RunnerRow = ({
  runner,
  marketTitle,
  minStake,
  maxStake,
  onOddsClick,
  marketStatus,
  expFulldata,
  primaryMarket,
}) => {
  const backOrder = [2, 1, 0];
  const isMarketSuspended =
    String(marketStatus || "").toUpperCase() === "SUSPENDED";
  const isRunnerStatusSuspended = runner.isSuspended;
  const isSuspendedForDisplay = isRunnerStatusSuspended || isMarketSuspended;

  const isClickableOdds = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0;
  };
  const getOddsButtonProps = (value) => {
    const disabled = isSuspendedForDisplay || !isClickableOdds(value);
    return {
      disabled,
      style: disabled
        ? {
            cursor: "not-allowed",
            pointerEvents: "none",
            opacity: 0.6,
            filter: "grayscale(25%)",
          }
        : undefined,
    };
  };

  const flash = useFlashOnChange({
    backPrices: runner.backPrices,
    backSizes: runner.backSizes,
    layPrices: runner.layPrices,
    laySizes: runner.laySizes,
    isSuspended: isSuspendedForDisplay,
  });

  const isToPlaced = primaryMarket?.market_type === "TO_BE_PLACED";

  return (
    <div
      className={`row py-1 mx-0 align-items-center${flash ? " odds-flash-row" : ""}`}
    >
      <div className="col-lg-6 col-md-5 col-8 px-0 horce-racing-details d-flex align-items-center justify-content-between pe-4">
        <div>
          {(runner.imageUrl || runner.imgId) && (
            <img
              src={runner.imageUrl || `${RACE_IMG_BASE}${runner.imgId}.png`}
              alt={runner.name}
            />
          )}
          <span className="marketevent">{runner.name}</span>

          <div className="jockey-detail">
            <span className="jockey-detail-box">
              <b>Jockey:</b>
              <span>{runner.metadata?.JOCKEY_NAME || runner.jockey}</span>
            </span>
            <span className="jockey-detail-box">
              <b>Age:</b>
              <span>{runner.age}</span>
            </span>
          </div>
        </div>

        {isToPlaced ? (
          <div>
            {expFulldata.win_value !== 0 && (
              <div>
                {expFulldata.win_value != null && (
                  <span className="mrkt-volume text-green">
                    <span
                      className={
                        Number(expFulldata.win_value) < 0
                          ? "text-red"
                          : "text-green"
                      }
                    >
                      {Number(expFulldata.win_value) > 0 ? "+" : ""}
                      {Number(expFulldata.win_value).toFixed(2)}
                    </span>
                  </span>
                )}
              </div>
            )}
            {expFulldata.win_value !== 0 && (
              <div>
                {expFulldata.loss_value != null && (
                  <span className="mrkt-volume text-green">
                    <span
                      className={
                        Number(expFulldata.loss_value) < 0
                          ? "text-red"
                          : "text-green"
                      }
                    >
                      {Number(expFulldata.loss_value) > 0 ? "+" : ""}
                      {Number(expFulldata.loss_value).toFixed(2)}
                    </span>
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div>
            {!isToPlaced && runner.userPlDisplay != null && (
              <span className="mrkt-volume text-green">
                <span
                  className={
                    Number(runner.userPlDisplay) < 0 ? "text-red" : "text-green"
                  }
                >
                  ({Number(runner.userPlDisplay) > 0 ? "+" : ""}
                  {Number(runner.userPlDisplay).toFixed(2)})
                </span>
              </span>
            )}
          </div>
        )}
      </div>
      <div className="col-lg-6 col-md-7 col-4 px-0">
        <div className="btn-group">
          <button
            className="back back-light d-md-block d-none"
            {...getOddsButtonProps(runner.backPrices[backOrder[0]])}
            onClick={() =>
              onOddsClick({
                selectionName: runner.name,
                marketTitle,
                side: "back",
                minStake,
                maxStake,
                odds: Number(runner.backPrices[backOrder[0]]) || 0,
                marketId: runner.marketId,
                selectionId: runner.selectionId,
              })
            }
          >
            <span>
              {isSuspendedForDisplay
                ? "SUSPEND"
                : runner.backPrices[backOrder[0]]}
            </span>
            <small>
              {isSuspendedForDisplay ? "" : runner.backSizes[backOrder[0]]}
            </small>
          </button>
          <button
            className="back back-light d-md-block d-none"
            {...getOddsButtonProps(runner.backPrices[backOrder[1]])}
            onClick={() =>
              onOddsClick({
                selectionName: runner.name,
                marketTitle,
                side: "back",
                minStake,
                maxStake,
                odds: Number(runner.backPrices[backOrder[1]]) || 0,
                marketId: runner.marketId,
                selectionId: runner.selectionId,
              })
            }
          >
            <span>
              {isSuspendedForDisplay
                ? "SUSPEND"
                : runner.backPrices[backOrder[1]]}
            </span>
            <small>
              {isSuspendedForDisplay ? "" : runner.backSizes[backOrder[1]]}
            </small>
          </button>
          <button
            className="back"
            {...getOddsButtonProps(runner.backPrices[backOrder[2]])}
            onClick={() =>
              onOddsClick({
                selectionName: runner.name,
                marketTitle,
                side: "back",
                minStake,
                maxStake,
                odds: Number(runner.backPrices[backOrder[2]]) || 0,
                marketId: runner.marketId,
                selectionId: runner.selectionId,
              })
            }
          >
            <span>
              {isSuspendedForDisplay
                ? "SUSPEND"
                : runner.backPrices[backOrder[2]]}
            </span>
            <small>
              {isSuspendedForDisplay ? "" : runner.backSizes[backOrder[2]]}
            </small>
          </button>
          <button
            className="lay"
            {...getOddsButtonProps(runner.layPrices[0])}
            onClick={() =>
              onOddsClick({
                selectionName: runner.name,
                marketTitle,
                side: "lay",
                minStake,
                maxStake,
                odds: Number(runner.layPrices[0]) || 0,
                marketId: runner.marketId,
                selectionId: runner.selectionId,
              })
            }
          >
            <span>
              {isSuspendedForDisplay ? "SUSPEND" : runner.layPrices[0]}
            </span>
            <small>{isSuspendedForDisplay ? "" : runner.laySizes[0]}</small>
          </button>
          <button
            className="d-md-block d-none lay lay-light"
            {...getOddsButtonProps(runner.layPrices[1])}
            onClick={() =>
              onOddsClick({
                selectionName: runner.name,
                marketTitle,
                side: "lay",
                minStake,
                maxStake,
                odds: Number(runner.layPrices[1]) || 0,
                marketId: runner.marketId,
                selectionId: runner.selectionId,
              })
            }
          >
            <span>
              {isSuspendedForDisplay ? "SUSPEND" : runner.layPrices[1]}
            </span>
            <small>{isSuspendedForDisplay ? "" : runner.laySizes[1]}</small>
          </button>
          <button
            className="d-md-block d-none lay lay-light"
            {...getOddsButtonProps(runner.layPrices[2])}
            onClick={() =>
              onOddsClick({
                selectionName: runner.name,
                marketTitle,
                side: "lay",
                minStake,
                maxStake,
                odds: Number(runner.layPrices[2]) || 0,
                marketId: runner.marketId,
                selectionId: runner.selectionId,
              })
            }
          >
            <span>
              {isSuspendedForDisplay ? "SUSPEND" : runner.layPrices[2]}
            </span>
            <small>{isSuspendedForDisplay ? "" : runner.laySizes[2]}</small>
          </button>
          {isSuspendedForDisplay && <div className="suspended"> SUSPEND</div>}
        </div>
      </div>
    </div>
  );
};

const RacingDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { subId: routeMatchId } = useParams();
  const location = useLocation();
  const allMatches = useSelector((state) => state.matches.matches);
  const favoriteMarkets = useSelector((state) => state.multiMarket?.markets);
  const { matchDetails, teamPosition, fancyLiability, selectedBet, loading } =
    useSelector((state) => state.game);
  const [isMatchOddsOpen, setIsMatchOddsOpen] = useState(true);
  const [showRules, setShowRules] = useState(false);

  const isLoggedIn = typeof window !== "undefined" ? isAuthenticated() : false;

  const [openBetsOpen, setOpenBetsOpen] = useState(false);

  const matchId = routeMatchId ? String(routeMatchId) : null;
  const marketIdFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const raw = params.get("marketId");
    return raw ? String(raw) : null;
  }, [location.search]);
  const marketStartTimeFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const raw = params.get("marketStartTime");
    return raw ? String(raw) : null;
  }, [location.search]);

  const marketId = useMemo(() => {
    if (marketIdFromQuery) return marketIdFromQuery;
    if (!matchId || !Array.isArray(allMatches) || allMatches.length === 0) {
      return null;
    }
    const found = allMatches.find((m) => {
      const candidateId = m?.match_id ?? m?.event_id ?? m?.id ?? m?._id ?? null;
      return candidateId != null && String(candidateId) === matchId;
    });
    return found?.market_id ?? found?.marketId ?? null;
  }, [allMatches, matchId, marketIdFromQuery]);

  // Reset betslip when user navigates to another racing market/match
  useEffect(() => {
    dispatch(clearSelectedBet());
  }, [dispatch, matchId, marketId]);

  const toggleOpenBets = () => {
    setOpenBetsOpen((v) => !v);
  };

  const openOpenBetsModal = useCallback(() => {
    setOpenBetsOpen(true);
  }, []);

  const closeOpenBetsModal = useCallback(() => {
    setOpenBetsOpen(false);
  }, []);

  const selectedMarketMeta = useMemo(() => {
    if (!matchId || !marketId || !Array.isArray(allMatches)) return null;
    return (
      allMatches.find((m) => {
        const candidateMatchId =
          m?.match_id ?? m?.event_id ?? m?.id ?? m?._id ?? null;
        const candidateMarketId = m?.market_id ?? m?.marketId ?? null;
        return (
          candidateMatchId != null &&
          candidateMarketId != null &&
          String(candidateMatchId) === String(matchId) &&
          String(candidateMarketId) === String(marketId)
        );
      }) || null
    );
  }, [allMatches, matchId, marketId]);

  useEffect(() => {
    if (!matchId || !isLoggedIn) return;
    dispatch(fetchMultiMarketDetails());
    dispatch(fetchMatchDetailsAsync({ matchId, marketId }));
    // dispatch(fetchFanciesAsync({ matchId }));
    dispatch(fetchOpenBetsAsync());
    dispatch(fetchTeamPositionAsync({ matchId }));
    // dispatch(fetchFancyLiabilityAsync({ matchId }));
  }, [dispatch, matchId, marketId, isLoggedIn]);

  const favoriteMarketKeySet = useMemo(() => {
    const set = new Set();
    for (const market of Array.isArray(favoriteMarkets)
      ? favoriteMarkets
      : []) {
      const favMatchId = market?.match_id ?? market?.matchId ?? null;
      const favMarketId =
        market?.market_id ?? market?.marketId ?? market?.id ?? null;
      if (favMatchId == null || favMarketId == null) continue;
      set.add(`${String(favMatchId)}::${String(favMarketId)}`);
    }
    return set;
  }, [favoriteMarkets]);

  const isMarkedFavorite = useMemo(
    () => (candidateMatchId, candidateMarketId) => {
      if (candidateMatchId == null || candidateMarketId == null) return false;
      return favoriteMarketKeySet.has(
        `${String(candidateMatchId)}::${String(candidateMarketId)}`,
      );
    },
    [favoriteMarketKeySet],
  );

  const pollMatchDetails = useCallback(() => {
    if (!matchId || !isLoggedIn) return;
    dispatch(fetchMatchDetailsAsync({ matchId, marketId }));
    // dispatch(fetchFanciesAsync({ matchId }));
  }, [dispatch, isLoggedIn, marketId, matchId]);

  useShortPolling(
    pollMatchDetails,
    getRacingDetailFanciesAndMatchDetailsInterval,
    !!matchId && isLoggedIn,
  );

  const matchDetailsArray = useMemo(
    () => (Array.isArray(matchDetails) ? matchDetails : []),
    [matchDetails],
  );

  const primaryMarket = matchDetailsArray[0] || {};

  const headerTitle = primaryMarket.match_name || "Race";
  const headerTitleName = primaryMarket.name;
  const marketStartDateTimeRaw =
    marketStartTimeFromQuery ||
    selectedMarketMeta?.market_start_time ||
    primaryMarket.market_start_time ||
    primaryMarket.open_date ||
    primaryMarket.match_date;
  const headerDateTime = formatDateTime(marketStartDateTimeRaw);
  const numberOfWinners =
    primaryMarket?.number_of_winners ??
    primaryMarket?.no_of_winners ??
    primaryMarket?.num_winners ??
    1;

  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => {
      setNowTs(Date.now());
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const preMatchWindowMs = 3 * 60 * 1000;
  const marketStartTs = useMemo(() => {
    if (!marketStartDateTimeRaw) return null;
    const parsed = new Date(marketStartDateTimeRaw).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }, [marketStartDateTimeRaw]);
  const countdownMs = useMemo(() => {
    if (!marketStartTs) return 0;
    return Math.max(marketStartTs - nowTs, 0);
  }, [marketStartTs, nowTs]);
  const countdownLabel = useMemo(
    () => getDisplayCountdown(countdownMs),
    [countdownMs],
  );
  const countdownDisplayLabel = countdownMs <= 0 ? "Started" : countdownLabel;
  // const isPreMatchBetWindowOpen = countdownMs <= 0;
  const apiBetStatus = String(primaryMarket?.status || "").toUpperCase();
  // Show "BET CLOSE" by default until we're within 3 minutes of the start time.
  const betStatusLabel =
    countdownMs > preMatchWindowMs ? "BET CLOSE" : apiBetStatus;
  const betStatusClassName = useMemo(() => {
    if (betStatusLabel === "OPEN" || betStatusLabel === "STARTED") {
      return "bet-status-label--open";
    }
    if (
      betStatusLabel === "SUSPENDED" ||
      betStatusLabel === "BET CLOSED" ||
      betStatusLabel === "BET CLOSE"
    ) {
      return "bet-status-label--closed";
    }
    return "bet-status-label--closed";
  }, [betStatusLabel]);
  const marketTitle =
    primaryMarket.market_name || primaryMarket.name || "Match Odds";
  const minStake = primaryMarket?.inplay
    ? primaryMarket?.market_min_stack
    : (primaryMarket?.market_advance_bet_min_stake ??
      primaryMarket?.market_min_stack ??
      "-");

  const isInplayMarket = primaryMarket?.inplay ?? false;

  const maxStake = primaryMarket?.inplay
    ? primaryMarket?.market_max_stack
    : (primaryMarket?.market_advance_bet_stake ??
      primaryMarket?.market_max_stack ??
      "-");
  const marketKey =
    primaryMarket?.market_id ??
    primaryMarket?.id ??
    primaryMarket?.marketId ??
    null;

  const marketStatus = primaryMarket?.status ?? "";
  const bookmarkContext = useMemo(() => {
    if (matchId == null || marketId == null) return null;
    return {
      matchId: String(matchId),
      marketId: String(marketId),
    };
  }, [matchId, marketId]);

  const [isBookmarked, setIsBookmarked] = useState(() => {
    const v =
      primaryMarket?.is_favourite ??
      primaryMarket?.is_favorite ??
      primaryMarket?.favorite ??
      false;
    return Boolean(Number(v) || v === true);
  });
  const [pendingBookmarkValue, setPendingBookmarkValue] = useState(null);

  useEffect(() => {
    const v =
      primaryMarket?.is_favourite ??
      primaryMarket?.is_favorite ??
      primaryMarket?.favorite ??
      false;
    const serverValueFromMarket = Boolean(Number(v) || v === true);
    const serverValueFromFavorites = isMarkedFavorite(matchId, marketKey);
    const serverValue = serverValueFromMarket || serverValueFromFavorites;

    // Keep optimistic state stable while polling responses are stale.
    if (pendingBookmarkValue !== null) {
      if (serverValue === pendingBookmarkValue) {
        setPendingBookmarkValue(null);
      }
      return;
    }

    setIsBookmarked(serverValue);
  }, [
    isMarkedFavorite,
    matchId,
    marketKey,
    pendingBookmarkValue,
    primaryMarket,
  ]);

  const dispatchSelectedBet = (betPayload) => {
    if (!betPayload?.marketId || !betPayload?.selectionId || !betPayload?.odds)
      return;
    dispatch(setSelectedBet(betPayload));
  };

  const runners = useMemo(() => {
    if (!isLoggedIn) return [];
    const source = Array.isArray(primaryMarket?.runners)
      ? primaryMarket.runners
      : Array.isArray(primaryMarket?.runner)
        ? primaryMarket.runner
        : [];

    const marketPositionRows =
      marketKey != null &&
      teamPosition &&
      typeof teamPosition === "object" &&
      !Array.isArray(teamPosition)
        ? teamPosition[marketKey] || teamPosition[String(marketKey)] || []
        : [];

    const positionBySelectionId = Array.isArray(marketPositionRows)
      ? marketPositionRows.reduce((acc, row) => {
          const key = row?.selection_id ?? row?.selectionId;
          if (key != null) {
            acc[String(key)] = row;
          }
          return acc;
        }, {})
      : {};

    const currentMarketIdStr = marketKey != null ? String(marketKey) : null;
    const selectedMarketIdStr =
      selectedBet?.marketId != null ? String(selectedBet.marketId) : null;
    const selectedSelectionIdStr =
      selectedBet?.selectionId != null ? String(selectedBet.selectionId) : null;
    const selectedStake = Number(selectedBet?.stake);
    const selectedOdds = Number(selectedBet?.odds);
    const side = String(selectedBet?.side || "").toLowerCase();
    const hasProjectedLiability =
      !!selectedBet &&
      !selectedBet?.isFancy &&
      !!currentMarketIdStr &&
      !!selectedMarketIdStr &&
      currentMarketIdStr === selectedMarketIdStr &&
      !!selectedSelectionIdStr &&
      Number.isFinite(selectedStake) &&
      selectedStake > 0 &&
      Number.isFinite(selectedOdds) &&
      selectedOdds > 1 &&
      (side === "back" || side === "lay");
    const winLossAmount = (selectedOdds - 1) * selectedStake;

    return source.map((runner, index) => {
      const backSource =
        runner?.back ||
        runner?.backs ||
        runner?.availableToBack ||
        runner?.ex?.availableToBack ||
        [];
      const laySource =
        runner?.lay ||
        runner?.lays ||
        runner?.availableToLay ||
        runner?.ex?.availableToLay ||
        [];
      const back = formatLadder(backSource);
      const lay = formatLadder(laySource);
      const status = String(runner?.status || "").toLowerCase();
      const selectionId =
        runner?.selectionId ??
        runner?.selection_id ??
        runner?.id ??
        runner?._id ??
        null;
      const positionRow =
        selectionId != null ? positionBySelectionId[String(selectionId)] : null;
      const userPlRaw = positionRow?.user_pl ?? positionRow?.win_loss ?? null;
      const baseUserPl =
        userPlRaw == null
          ? null
          : typeof userPlRaw === "number"
            ? userPlRaw
            : Number(userPlRaw) || 0;
      let userPlDisplay = baseUserPl;
      if (hasProjectedLiability && selectionId != null) {
        const isSelectedRunner = String(selectionId) === selectedSelectionIdStr;
        const delta =
          side === "back"
            ? isSelectedRunner
              ? winLossAmount
              : -selectedStake
            : isSelectedRunner
              ? -winLossAmount
              : selectedStake;
        userPlDisplay = (baseUserPl ?? 0) + delta;
      }

      return {
        position: index + 1,
        name:
          runner?.name || runner?.runner_name || runner?.selection_name || "-",
        selectionId,
        marketId: marketKey,
        imageUrl: runner?.metadata?.COLOURS_FILENAME || null,
        imgId: runner?.imgId || runner?.img_id || runner?.image_id || null,
        jockey:
          runner?.jockey ||
          runner?.jockey_name ||
          runner?.metadata?.JOCKEY_NAME ||
          "-",
        age: runner?.age ?? runner?.horse_age ?? runner?.metadata?.AGE ?? "-",
        isSuspended:
          status.includes("suspend") ||
          status.includes("closed") ||
          status.includes("inactive"),
        backPrices: back.prices,
        backSizes: back.sizes,
        layPrices: lay.prices,
        laySizes: lay.sizes,
        userPl: baseUserPl,
        userPlDisplay,
      };
    });
  }, [marketKey, primaryMarket, teamPosition, fancyLiability, selectedBet]);

  const handleBookmarkClick = (e) => {
    e.preventDefault?.();
    e.stopPropagation?.();
    if (!bookmarkContext) return;
    const next = !isBookmarked;
    setPendingBookmarkValue(next);
    setIsBookmarked(next);

    const eventId = marketKey || bookmarkContext.marketId;
    if (eventId && matchId) {
      dispatch(
        toggleFavoriteMarket({
          eventId,
          matchId,
          isAdd: next,
          isFancy: false,
        }),
      )
        .unwrap()
        .then(() => {
          dispatch(fetchMultiMarketDetails());
          toast.success(next ? "Added to favorites" : "Removed from favorites");
        })
        .catch((err) => {
          setPendingBookmarkValue(null);
          setIsBookmarked((prev) => !prev);
          toast.error(
            err?.message || err?.msg || "Failed to update favorite on server",
          );
        });
      return;
    }

    toast.success(next ? "Added to favorites" : "Removed from favorites");
  };

  if (!isLoggedIn) {
    return (
      <section className="section listing_page">
        <div className="row">
          <div className="col-md-12 col-lg-8 detail-center-column">
            <div className="middle-page-content details-page">
              <div className="card-header game-heading">
                <span className="card-header-title">
                  Please log in to view this page
                </span>
              </div>
              <div className="card-body py-4 text-center">
                <p className="mb-3">
                  You need to be logged in to view match details and place bets.
                </p>
                <button
                  type="button"
                  className="btn btn-login"
                  onClick={() => {
                    if (
                      typeof window !== "undefined" &&
                      typeof window.openLoginDialog === "function"
                    ) {
                      window.openLoginDialog();
                    } else {
                      navigate("/login");
                    }
                  }}
                >
                  Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section listing_page">
      <div className="row">
        <div className="col-md-12 col-lg-8 detail-center-column">
          <div className="middle-page-content details-page">
            <div className="card-header game-heading">
              <span className="card-header-title">
                {" "}
                {headerTitle} {headerTitleName}
              </span>
              {headerDateTime && (
                <span className="date-time">({headerDateTime})</span>
              )}
              <Link
                to="#"
                className="best_link d-lg-none"
                onClick={(e) => {
                  e.preventDefault?.();
                  e.stopPropagation?.();
                  openOpenBetsModal();
                }}
                aria-label="Open bets"
              >
                Bets
              </Link>
            </div>
            {openBetsOpen ? (
              <>
                <div
                  className="modal-backdrop fade show"
                  onClick={closeOpenBetsModal}
                />
                <div
                  className="modal fade show d-block"
                  tabIndex="-1"
                  role="dialog"
                  aria-modal="true"
                  onClick={closeOpenBetsModal}
                >
                  <div
                    className="modal-dialog modal-dialog-centered modal-lg"
                    role="document"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title text-uppercase">
                          Open Bets
                        </h5>
                        <button
                          type="button"
                          aria-label="Close"
                          className="btn"
                          style={{ marginLeft: "auto" }}
                          onClick={closeOpenBetsModal}
                        >
                          <span aria-hidden="true">X</span>
                        </button>
                      </div>
                      <div className="modal-body">
                        <PlaceBetOpenBets
                          matchId={matchId}
                          showPlaceBet={false}
                          // showOpenBets
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
            <div className="image-wrapper horseBg">
              <div className="image-inner-div">
                <span className="inner-image-div">
                  <h5 className="sm-fs-13" style={{ color: "#fff" }}>
                    Back
                  </h5>
                  <h5 className="sm-fs-13" style={{ color: "#fff" }}>
                    enabled
                  </h5>
                </span>
                <span className="inner-image-div">
                  <h5 className="sm-fs-13" style={{ color: "#fff" }}>
                    {headerTitle}
                  </h5>
                  <h5 className="sm-fs-13" style={{ color: "#fff" }}>
                    {headerTitleName}
                  </h5>
                </span>
                <span className="inner-image-div">
                  <h5 className="sm-fs-13" style={{ color: "#fff" }}>
                    Box
                  </h5>
                  <h5 className="sm-fs-13" style={{ color: "#fff" }}>
                    Market
                  </h5>
                </span>
              </div>
            </div>
            <div className="timeAndStatus racing-time-status mb-2">
              <div className="racing-time-status__item">
                <span className="racing-time-status__label">winners:</span>
                <span className="racing-time-status__value">
                  {" "}
                  {numberOfWinners}
                </span>
              </div>
              <div className="racing-time-status__item racing-time-status__item--status">
                <span
                  className={`racing-time-status__value bet-status-label ${betStatusClassName}`}
                >
                  {betStatusLabel}
                </span>
              </div>
              <div className="racing-time-status__item racing-time-status__item--time">
                <span className="racing-time-status__label">Time:</span>
                <span className="racing-time-status__value racing-time-status__value--pulse">
                  {" "}
                  {countdownDisplayLabel}
                </span>
              </div>
            </div>

            {/* Open Bets now opens via the header "Bets" modal on mobile */}

            <div
              id="collapseBasic"
              aria-hidden="true"
              className="collapse"
              style={{ display: "none" }}
            ></div>
            <div id="ScorecardCollapse" className="collapse show">
              <div className="sr-widget-1"></div>
            </div>
            <div>
              <div className="empty1">
                <div className="market_section">
                  <div className="horse-market">
                    <p>
                      <span className="mrkname">{marketTitle} </span>
                      <button
                        type="button"
                        className="add-pin"
                        aria-label={
                          isBookmarked
                            ? "Remove bookmark from favorites"
                            : "Add bookmark to favorites"
                        }
                        aria-pressed={isBookmarked}
                        onClick={handleBookmarkClick}
                      >
                        <i
                          className={
                            isBookmarked
                              ? "bi bi-bookmark-fill"
                              : "bi bi-bookmark"
                          }
                        />
                      </button>
                      <button className="btn btn_speedcash">speed cash</button>{" "}
                      --&gt;{" "}
                      <span>
                        {isInplayMarket && (
                          <button className="btn-cashout">In Play</button>
                        )}
                      </span>
                      <span className="min-max">
                        {" "}
                        Min: {minStake} | Max: {maxStake}{" "}
                        <button
                          type="button"
                          className="ms-2 game-rules-icon"
                          aria-label="Match rules"
                          onClick={(e) => {
                            e.preventDefault?.();
                            e.stopPropagation?.();
                            setShowRules(true);
                          }}
                        >
                          <span>
                            <i className="bi bi-info-square-fill"></i>
                          </span>
                        </button>
                        <button
                          type="button"
                          aria-expanded={isMatchOddsOpen}
                          aria-controls="Match_Odds0"
                          aria-label="Toggle odds"
                          className={`an-collapse p-0 border-0 bg-transparent ${isMatchOddsOpen ? "" : "collapsed"}`}
                          onClick={() => setIsMatchOddsOpen((prev) => !prev)}
                        />
                      </span>
                    </p>
                    <MatchRulesModal
                      show={showRules}
                      onClose={() => setShowRules(false)}
                      sectionHeading="Match"
                    />
                    <div
                      className={isMatchOddsOpen ? "collapse show" : "collapse"}
                      id="Match_Odds0"
                    >
                      <div>
                        <div className="row align-items-center oddsHeader">
                          <div className="col-lg-6 col-md-5 col-8 px-0"></div>
                          <div className="col-lg-6 col-md-7 col-4 px-0">
                            <div className="btn-group">
                              <button className="ex-bl d-none d-md-block"></button>
                              <button className="ex-bl d-none d-md-block"></button>
                              <button className="back">back</button>
                              <button className="lay">lay</button>
                              <button className="ex-bl d-none d-md-block"></button>
                              <button className="ex-bl d-none d-md-block"></button>
                            </div>
                          </div>
                        </div>
                        {runners.map((runner) => {
                          const isSelectedRunner =
                            selectedBet &&
                            String(selectedBet?.marketId) ===
                              String(marketKey) &&
                            String(selectedBet?.selectionId) ===
                              String(runner?.selectionId);

                          const expFulldata = teamPosition[
                            String(marketKey)
                          ]?.find(
                            (item) => item?.selection_id == runner?.selectionId,
                          );

                          // console.log(expFulldata, "expFulldata")
                          return (
                            <React.Fragment
                              key={`${runner.position}-${runner.selectionId}`}
                            >
                              <RunnerRow
                                runner={runner}
                                marketStatus={marketStatus}
                                marketTitle={marketTitle}
                                minStake={Number(minStake) || 0}
                                maxStake={Number(maxStake) || 0}
                                onOddsClick={dispatchSelectedBet}
                                expFulldata={expFulldata}
                                primaryMarket={primaryMarket}
                              />
                              {isSelectedRunner && (
                                <div className="row mx-0">
                                  <div className="col-12 px-0 mt-1 d-lg-none">
                                    <PlaceBetOpenBets
                                      inline
                                      showOpenBets={false}
                                      matchId={matchId}
                                      marketId={marketKey}
                                    />
                                  </div>
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {!loading && runners.length === 0 && (
                          <div className="row mx-0 align-items-center">
                            <div className="col-12 px-2 py-2 text-center">
                              No markets
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-12 col-lg-4 detail-right-column d-none d-lg-block px-0">
          <PlaceBetOpenBets matchId={matchId} marketId={marketId} />
        </div>
      </div>
    </section>
  );
};

export default RacingDetail;
